import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { Subscription } from 'rxjs';
import ProjectPermission from 'models/project-permission/project-permission.model';
import Project from 'models/project/project.model';
import User from 'models/user/user.model';
import { ProjectUserPermissionsComponent } from 'modules/project/project-user-permissions/project-user-permissions.component';
import { UpdateProjectComponent } from 'modules/project/update-project/update-project.component';
import { ProjectService } from 'services/project/project.service';
import { UserService } from 'services/user/user.service';

@Component({
  selector: 'app-edit-project',
  templateUrl: './edit-project.component.html',
  styleUrls: ['./edit-project.component.scss'],
})
export class EditProjectComponent implements OnInit, OnDestroy {
  @ViewChild(MatAccordion) accordion: MatAccordion;

  public instance: Project = new Project();
  public user: User = new User();
  public projectUserList: User[] = [];
  public userList: User[] = [];
  public projectPermission: ProjectPermission = new ProjectPermission();

  private projectSubscription: Subscription;
  private userSubscription: Subscription;
  private permissionSubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    public userService: UserService,
    private dialog: MatDialog,
    public projectService: ProjectService,
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(async (params) => {
      if (params.id) {
        await this.projectService.select(params.id);
      }
      this.user = this.userService.getCurrentUser().value;
      this.projectSubscription = this.projectService.getSelectedInstance().subscribe((instance) => {
        this.instance = instance;
        this.getUserList();
        if (this.user.username !== 'sysadmin') {
          this.projectService.getPerms(this.instance, this.user);
        }
      });
      if (this.user.username !== 'sysadmin') {
        this.permissionSubscription = this.projectService.getSelectedPermissions().subscribe((permissions) => {
          this.projectPermission = permissions
        });
      }
    });
  }

  openPermissionsModal(user: User) {
    this.dialog.open(
      ProjectUserPermissionsComponent,
      {
        data: { user, project: this.instance },
      },
    );
  }

  openEditModal() {
    const dialogRef = this.dialog.open(
      UpdateProjectComponent,
      {
        minWidth: '400px',
        data: { project: this.instance },
      },
    );
    dialogRef.afterClosed().subscribe((newName) => {
      if (newName) {
        this.projectService.update(new Project({ ...this.instance, name: newName }))
          .then(() => this.projectService.select(this.instance.id));
      }
    })
  }

  async removeUser(user: User) {
    if (await this.projectService.removeUser(this.instance, user)) {
      await this.projectService.select(this.instance.id);
    }
  }

  async addUser(user: User) {
    if (await this.projectService.addUser(this.instance, user)) {
      await this.projectService.select(this.instance.id);
    }
  }

  private async getUserList() {
    if (this.instance.organizationUnit.name) {
      this.projectUserList = await this.userService.findByOu(this.instance.organizationUnit);
      const projectUserList: User[] = await this.projectService.userList(this.instance);
      const idList: string[] = [];
      for (let i = 0; i < projectUserList.length; i++) {
        idList.push(projectUserList[i].username);
      }
      this.userList = this.projectUserList.filter((u) => !u.isSuperAdminIn(this.instance.organizationUnit.name) && idList.includes(u.username));
    }
  }

  async deleteProject() {
    await this.projectService.delete(this.instance);
    this.router.navigate(['/ou/get/' + this.instance.organizationUnit.id]);
  }

  back() {
    this.router.navigate(['/project/get'], { queryParams: { id: this.instance.id } })
  }

  ngOnDestroy() {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.projectSubscription) {
      this.projectSubscription.unsubscribe();
    }
    if (this.permissionSubscription) {
      this.permissionSubscription.unsubscribe();
    }
  }
}
